<template>
  <div class="rag-dashboard">
    <div class="dashboard-toolbar">
      <label class="period-label">Период графиков</label>
      <Select
        v-model="chartDays"
        :options="periodOptions"
        option-label="label"
        option-value="value"
        class="period-select"
        @change="loadCharts"
      />
      <ButtonVue label="Обновить" icon="pi pi-refresh" severity="secondary" :loading="loading" @click="loadAll" />
    </div>

    <div v-if="loading && !summary" class="loading-wrap">
      <ProgressSpinner />
    </div>

    <template v-else>
      <div class="stat-row">
        <Card class="stat-card">
          <template #title>Запросы 24ч</template>
          <template #content><span class="stat-value">{{ summary.requests_24h ?? 0 }}</span></template>
        </Card>
        <Card class="stat-card">
          <template #title>Запросы 7д</template>
          <template #content><span class="stat-value">{{ summary.requests_7d ?? 0 }}</span></template>
        </Card>
        <Card class="stat-card">
          <template #title>Запросы 30д</template>
          <template #content><span class="stat-value">{{ summary.requests_30d ?? 0 }}</span></template>
        </Card>
        <Card class="stat-card">
          <template #title>Всего сессий</template>
          <template #content><span class="stat-value">{{ summary.total_sessions ?? 0 }}</span></template>
        </Card>
      </div>

      <div class="kpi-row">
        <Card class="kpi-card">
          <template #title>Avg Latency (30д)</template>
          <template #content><span class="kpi-value">{{ summary.avg_latency_ms ?? 0 }} ms</span></template>
        </Card>
        <Card class="kpi-card">
          <template #title>Avg Tokens / request</template>
          <template #content><span class="kpi-value">{{ summary.avg_tokens_per_request ?? 0 }}</span></template>
        </Card>
        <Card class="kpi-card">
          <template #title>Total Requests (30д)</template>
          <template #content><span class="kpi-value">{{ summary.requests_30d ?? 0 }}</span></template>
        </Card>
        <Card class="kpi-card">
          <template #title>Active Users (24ч)</template>
          <template #content><span class="kpi-value">{{ summary.active_users_24h ?? 0 }}</span></template>
        </Card>
      </div>

      <div class="charts-grid">
        <Card>
          <template #title>Запросов по дням</template>
          <template #content>
            <Chart type="line" :data="requestsChart" :options="lineOptions" class="chart" />
          </template>
        </Card>
        <Card>
          <template #title>Средний latency по дням</template>
          <template #content>
            <Chart type="line" :data="latencyChart" :options="lineOptions" class="chart" />
          </template>
        </Card>
        <Card>
          <template #title>Расход токенов по дням</template>
          <template #content>
            <Chart type="line" :data="tokensChart" :options="lineOptions" class="chart" />
          </template>
        </Card>
        <Card>
          <template #title>Топ mode</template>
          <template #content>
            <Chart type="bar" :data="modesChart" :options="barOptions" class="chart" />
          </template>
        </Card>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import Card from 'primevue/card';
import Chart from 'primevue/chart';
import Select from 'primevue/select';
import ProgressSpinner from 'primevue/progressspinner';
import { useToast } from 'primevue/usetoast';
import {
  getDashboardSummary,
  getDashboardTimeseries,
  getDashboardModes
} from '@/services/ragMonitorApi';

const toast = useToast();
const loading = ref(false);
const summary = ref({});
const timeseries = ref([]);
const modes = ref([]);
const chartDays = ref(30);

const periodOptions = [
  { label: '7 дней', value: 7 },
  { label: '30 дней', value: 30 },
  { label: '90 дней', value: 90 }
];

function chartScaleOptions() {
  const tick = '#6b7280';
  const grid = 'rgba(0, 0, 0, 0.06)';
  return {
    ticks: { color: tick, maxRotation: 45, minRotation: 0 },
    grid: { color: grid }
  };
}

const lineOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: chartScaleOptions(),
    y: chartScaleOptions()
  }
}));

const barOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  plugins: { legend: { display: false } },
  scales: {
    x: chartScaleOptions(),
    y: chartScaleOptions()
  }
}));

function formatDay(day) {
  if (!day) return '';
  const d = new Date(day);
  return Number.isNaN(d.getTime()) ? String(day) : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

const labels = computed(() => timeseries.value.map((p) => formatDay(p.day)));

const requestsChart = computed(() => ({
  labels: labels.value,
  datasets: [{
    label: 'Requests',
    data: timeseries.value.map((p) => p.requests),
    borderColor: '#3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    fill: true,
    tension: 0.3
  }]
}));

const latencyChart = computed(() => ({
  labels: labels.value,
  datasets: [{
    label: 'Latency ms',
    data: timeseries.value.map((p) => p.avg_latency_ms),
    borderColor: '#f59e0b',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    fill: true,
    tension: 0.3
  }]
}));

const tokensChart = computed(() => ({
  labels: labels.value,
  datasets: [{
    label: 'Tokens',
    data: timeseries.value.map((p) => Number(p.total_tokens) || 0),
    borderColor: '#10b981',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    fill: true,
    tension: 0.3
  }]
}));

const modesChart = computed(() => ({
  labels: modes.value.map((m) => m.mode),
  datasets: [{
    label: 'Count',
    data: modes.value.map((m) => m.count),
    backgroundColor: '#6366f1'
  }]
}));

async function loadSummary() {
  summary.value = await getDashboardSummary();
}

async function loadCharts() {
  const [ts, md] = await Promise.all([
    getDashboardTimeseries({ days: chartDays.value }),
    getDashboardModes({ days: chartDays.value, limit: 10 })
  ]);
  timeseries.value = ts.points || [];
  modes.value = md.modes || [];
}

async function loadAll() {
  loading.value = true;
  try {
    await Promise.all([loadSummary(), loadCharts()]);
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: e.message, life: 5000 });
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadAll();
});
</script>

<style scoped>
.rag-dashboard {
  padding: 0 0 1.5rem;
}

.dashboard-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.period-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--p-text-color);
}

.period-select {
  min-width: 140px;
}

.stat-row,
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.stat-value,
.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--p-text-color);
}

.rag-dashboard :deep(.p-card) {
  border: 1px solid var(--p-content-border-color);
  box-shadow: none;
}

.rag-dashboard :deep(.p-card-title) {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
}

.rag-dashboard :deep(.p-card-body) {
  padding-top: 0.25rem;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.chart {
  height: 220px;
}

.loading-wrap {
  display: flex;
  justify-content: center;
  padding: 3rem;
}
</style>
